import { useState } from 'react'
import type {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  RowSelectionState,
} from '@tanstack/react-table'
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  MoreHorizontal,
  Pencil,
  Trash2,
  CheckCircle,
  Wrench,
  FileCheck,
  Building2,
  Warehouse,
  ParkingCircle,
  Briefcase,
  Square,
  CircleDot,
  Tent,
  Package,
} from 'lucide-react'
import {
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTablePagination,
  DataTableToolbar,
} from '@/components/data-table'
import type { UnitTableRow, UnitStatus, UnitType } from '@/types/units'
import {
  UNIT_STATUS_LABELS,
  UNIT_STATUSES,
  UNIT_TYPE_LABELS,
  UNIT_TYPES,
} from '@/types/units'

interface UnitsTableProps {
  /** Unit data to display */
  data: UnitTableRow[]
  /** Loading state */
  isLoading: boolean
  /** Callback when edit action is triggered */
  onEdit: (id: string) => void
  /** Callback when delete action is triggered */
  onDelete: (id: string, unitCode: string) => void
}

/** Badge variant mapping for unit status */
const statusVariants: Record<UnitStatus, 'default' | 'secondary' | 'outline'> = {
  available: 'default',
  leased: 'secondary',
  maintenance: 'outline',
}

/** Badge variant mapping for unit type */
const typeVariants: Record<UnitType, 'default' | 'secondary' | 'outline'> = {
  conventional_hangar: 'default',
  box_hangar: 'default',
  t_hangar: 'default',
  shade: 'outline',
  office: 'secondary',
  tiedown: 'outline',
  ramp: 'outline',
  storage: 'secondary',
  shop: 'secondary',
  custom: 'secondary',
}

/** Icon mapping for unit types */
const typeIcons: Record<UnitType, React.ComponentType<{ className?: string }>> = {
  conventional_hangar: Warehouse,
  box_hangar: Warehouse,
  t_hangar: Warehouse,
  shade: Tent,
  office: Briefcase,
  tiedown: ParkingCircle,
  ramp: Square,
  storage: Package,
  shop: Wrench,
  custom: Building2,
}

/** Status filter options with icons */
const statusFilterOptions = UNIT_STATUSES.map((status) => ({
  label: UNIT_STATUS_LABELS[status],
  value: status,
  icon: status === 'available' ? CheckCircle : status === 'leased' ? FileCheck : Wrench,
}))

/** Type filter options with icons */
const typeFilterOptions = UNIT_TYPES.map((type) => ({
  label: UNIT_TYPE_LABELS[type],
  value: type,
  icon: typeIcons[type] || CircleDot,
}))

export function UnitsTable({
  data,
  isLoading,
  onEdit,
  onDelete,
}: UnitsTableProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [globalFilter, setGlobalFilter] = useState('')

  // Extract unique buildings for filter
  const buildingFilterOptions = Array.from(
    new Map(data.map((u) => [u.area_id, { name: u.area_name, code: u.area_code }])).entries()
  ).map(([id, building]) => ({
    label: `${building.name} (${building.code})`,
    value: id,
    icon: Building2,
  }))

  const columns: ColumnDef<UnitTableRow>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'unit_code',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Unit Code" />
      ),
      cell: ({ row }) => (
        <span className="font-mono font-medium">{row.getValue('unit_code')}</span>
      ),
      enableHiding: false,
    },
    {
      accessorKey: 'area_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Building" />
      ),
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.getValue('area_name')}</div>
          <div className="text-xs text-muted-foreground">
            {row.original.area_code}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'area_id',
      header: 'Building Filter',
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
      enableHiding: true,
    },
    {
      accessorKey: 'location_name',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Location" />
      ),
      cell: ({ row }) => {
        const locationName = row.getValue('location_name') as string | null
        return locationName ? (
          <span className="text-sm">{locationName}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: 'type',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Type" />
      ),
      cell: ({ row }) => {
        const type = row.getValue('type') as UnitType | null
        if (!type) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <Badge variant={typeVariants[type] || 'default'}>
            {UNIT_TYPE_LABELS[type] || type}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        const type = row.getValue(id) as string | null
        return type ? value.includes(type) : false
      },
    },
    {
      accessorKey: 'status',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Status" />
      ),
      cell: ({ row }) => {
        const status = row.getValue('status') as UnitStatus
        return (
          <Badge variant={statusVariants[status] || 'default'}>
            {UNIT_STATUS_LABELS[status] || status}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        return value.includes(row.getValue(id))
      },
    },
    {
      id: 'dimensions',
      header: 'Dimensions (ft)',
      cell: ({ row }) => {
        const { door_width, depth, tail_width } = row.original
        if (!door_width && !depth) {
          return <span className="text-muted-foreground">—</span>
        }
        return (
          <div className="text-sm text-muted-foreground">
            <span>Door: {door_width ?? '—'}</span>
            <span className="ml-2">Depth: {depth ?? '—'}</span>
            {tail_width && <span className="ml-2">Tail: {tail_width}</span>}
          </div>
        )
      },
    },
    {
      accessorKey: 'is_leasable',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title="Leasable" />
      ),
      cell: ({ row }) => {
        const isLeasable = row.getValue('is_leasable') as boolean
        return (
          <Badge variant={isLeasable ? 'default' : 'secondary'}>
            {isLeasable ? 'Yes' : 'No'}
          </Badge>
        )
      },
      filterFn: (row, id, value: string[]) => {
        if (!value || value.length === 0) return true
        const isLeasable = row.getValue(id) as boolean
        return value.includes(isLeasable ? 'true' : 'false')
      },
    },
    {
      id: 'pricing',
      header: 'Monthly Rate',
      cell: ({ row }) => {
        const { pricing_override, unit_monthly_rate } = row.original
        if (!pricing_override || !unit_monthly_rate) {
          return <span className="text-muted-foreground text-sm">Building default</span>
        }
        return (
          <span className="font-mono text-sm">
            ${unit_monthly_rate.toLocaleString()}
          </span>
        )
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(row.original.id)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => onDelete(row.original.id, row.original.unit_code)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
      enableHiding: false,
    },
  ]

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    globalFilterFn: (row, _columnId, filterValue) => {
      const search = filterValue.toLowerCase()
      const unitCode = (row.original.unit_code || '').toLowerCase()
      const buildingName = (row.original.area_name || '').toLowerCase()
      const buildingCode = (row.original.area_code || '').toLowerCase()
      const locationName = (row.original.location_name || '').toLowerCase()
      return (
        unitCode.includes(search) ||
        buildingName.includes(search) ||
        buildingCode.includes(search) ||
        locationName.includes(search)
      )
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility: {
        ...columnVisibility,
        area_id: false, // Hide the filter column
      },
      rowSelection,
      globalFilter,
    },
    initialState: {
      pagination: {
        pageSize: 15,
      },
      columnVisibility: {
        area_id: false,
      },
    },
  })

  // Export to CSV
  const handleExport = () => {
    const headers = [
      'Unit Code',
      'Building',
      'Building Code',
      'Location',
      'Type',
      'Status',
      'Door Width',
      'Depth',
      'Tail Width',
      'Leasable',
      'Monthly Rate',
    ]
    const rows = table.getFilteredRowModel().rows.map((row) => [
      row.original.unit_code,
      row.original.area_name,
      row.original.area_code,
      row.original.location_name || '',
      row.original.type ? UNIT_TYPE_LABELS[row.original.type] || row.original.type : '',
      UNIT_STATUS_LABELS[row.original.status] || row.original.status,
      row.original.door_width?.toString() || '',
      row.original.depth?.toString() || '',
      row.original.tail_width?.toString() || '',
      row.original.is_leasable ? 'Yes' : 'No',
      row.original.pricing_override && row.original.unit_monthly_rate
        ? row.original.unit_monthly_rate.toString()
        : 'Building default',
    ])

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(','))
      .join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `units_${new Date().toISOString().split('T')[0]}.csv`
    link.click()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar with search, filters, and view options */}
      <DataTableToolbar
        table={table}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        filterPlaceholder="Search units..."
        onExport={handleExport}
        exportLabel="Export CSV"
      >
        <DataTableFacetedFilter
          column={table.getColumn('type')}
          title="Type"
          options={typeFilterOptions}
        />
        <DataTableFacetedFilter
          column={table.getColumn('status')}
          title="Status"
          options={statusFilterOptions}
        />
        {buildingFilterOptions.length > 1 && (
          <DataTableFacetedFilter
            column={table.getColumn('area_id')}
            title="Building"
            options={buildingFilterOptions}
          />
        )}
      </DataTableToolbar>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    Loading units...
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center"
                >
                  <div className="text-muted-foreground">
                    No units found. Configure units in your buildings to get started.
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Enhanced Pagination */}
      <DataTablePagination table={table} />
    </div>
  )
}
